/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// 折扣码功能
document.addEventListener('DOMContentLoaded', function() {
    var DISCOUNT_MESSAGE_KEY = 'mini-cart-discount-message';
    var discountRequestInFlight = false;

    function getDiscountMessageEl() {
      return document.querySelector('[data-discount-message]');
    }

    function getDiscountMessageText(key, fallback) {
      var el = getDiscountMessageEl();
      if (!el) return fallback;
      return el.getAttribute('data-msg-' + key) || fallback;
    }

    function showDiscountMessage(type, text) {
      var el = getDiscountMessageEl();
      if (!el) return;
      el.hidden = false;
      el.className = 'discount-code-message discount-code-message--' + type;
      el.textContent = text;
    }

    function clearDiscountMessage() {
      var el = getDiscountMessageEl();
      if (!el) return;
      el.hidden = true;
      el.textContent = '';
      el.className = 'discount-code-message';
    }

    function queueDiscountMessage(type, text) {
      try {
        sessionStorage.setItem(DISCOUNT_MESSAGE_KEY, JSON.stringify({ type: type, text: text }));
      } catch (e) {}
    }

    function restoreQueuedDiscountMessage() {
      try {
        var raw = sessionStorage.getItem(DISCOUNT_MESSAGE_KEY);
        if (!raw) return;
        sessionStorage.removeItem(DISCOUNT_MESSAGE_KEY);
        var payload = JSON.parse(raw);
        if (payload && payload.text) {
          showDiscountMessage(payload.type || 'success', payload.text);
        }
      } catch (e) {}
    }

    function getCartRoot() {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) {
        return Shopify.routes.root;
      }
      if (window.themeVariables && themeVariables.routes && themeVariables.routes.rootUrl) {
        return themeVariables.routes.rootUrl;
      }
      return '/';
    }

    function findDiscountCodeEntry(cart, code) {
      if (!cart || !code) return null;
      var normalizedCode = code.toUpperCase();
      var discountCodes = cart.discount_codes || [];
      for (var i = 0; i < discountCodes.length; i++) {
        var entry = discountCodes[i];
        if (entry && entry.code && entry.code.toUpperCase() === normalizedCode) {
          return entry;
        }
      }
      return null;
    }

    function hasLineItemDiscountForCode(cart, code) {
      if (!cart || !code) return false;
      var normalizedCode = code.toUpperCase();
      var items = cart.items || [];

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var discounts = item.discounts || [];
        for (var d = 0; d < discounts.length; d++) {
          var discountTitle = (discounts[d].title || '').toUpperCase();
          if (discountTitle === normalizedCode || discountTitle.indexOf(normalizedCode) !== -1) {
            return true;
          }
        }

        var allocations = item.line_level_discount_allocations || [];
        for (var a = 0; a < allocations.length; a++) {
          var application = allocations[a].discount_application || allocations[a];
          var title = ((application && (application.title || application.code)) || '').toUpperCase();
          if (title === normalizedCode || title.indexOf(normalizedCode) !== -1) {
            return true;
          }
        }
      }

      var cartApps = cart.cart_level_discount_applications || [];
      for (var j = 0; j < cartApps.length; j++) {
        var cartTitle = (cartApps[j].title || cartApps[j].code || '').toUpperCase();
        if (cartTitle === normalizedCode || cartTitle.indexOf(normalizedCode) !== -1) {
          return true;
        }
      }

      return false;
    }

    // ok | not_eligible | invalid
    function getDiscountApplyStatus(cart, code) {
      if (hasLineItemDiscountForCode(cart, code)) {
        return 'ok';
      }

      var entry = findDiscountCodeEntry(cart, code);
      if (entry) {
        return entry.applicable ? 'ok' : 'not_eligible';
      }

      return 'invalid';
    }

    function updateCartDiscount(code) {
      var root = getCartRoot();
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ discount: code || '' })
      }).then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to update cart discount');
        }
        return response.json();
      }).then(function(cart) {
        // 再拉一次 cart.js，避免 update 响应偶发未带上最新折扣状态
        return fetch(root + 'cart.js', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        }).then(function(response) {
          if (!response.ok) {
            return cart;
          }
          return response.json();
        }).catch(function() {
          return cart;
        });
      });
    }

    function refreshMiniCart() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
      // 抽屉重渲染后恢复提示文案
      setTimeout(restoreQueuedDiscountMessage, 250);
    }

    // 监听购物车变化事件
    document.addEventListener('cart:updated', function(event) {
      const cartData = event.detail.cart;
      
      // 如果购物车为空，清除折扣码
      if (cartData && cartData.item_count === 0) {
        updateCartDiscount('').catch(function(error) {
          console.error('Error clearing discount code:', error);
        });
      }
    });
    
    // 使用事件委托来处理动态加载的元素
    document.addEventListener('click', function(e) {
      if (e.target.matches('[data-action="apply-discount"]')) {
        e.preventDefault();

        if (discountRequestInFlight) {
          return;
        }
        
        const discountCodeInput = document.getElementById('discount-code-input');
        const applyDiscountBtn = e.target;
        
        if (!discountCodeInput) {
          return;
        }
        
        const discountCode = discountCodeInput.value.trim();
        
        if (!discountCode) {
          showDiscountMessage('error', getDiscountMessageText('empty', 'Please enter a discount code'));
          return;
        }

        clearDiscountMessage();
        discountRequestInFlight = true;
        applyDiscountBtn.disabled = true;
        const originalText = applyDiscountBtn.textContent;
        applyDiscountBtn.textContent = getDiscountMessageText('applying', 'Applying...');
  
        updateCartDiscount(discountCode)
        .then(function(data) {
          var status = getDiscountApplyStatus(data, discountCode);

          if (status === 'ok') {
            queueDiscountMessage(
              'success',
              getDiscountMessageText('success', 'Discount code applied successfully')
            );
            refreshMiniCart();
            return;
          }

          if (status === 'not_eligible') {
            showDiscountMessage(
              'error',
              getDiscountMessageText(
                'not-eligible',
                'This discount code cannot be used for the current cart (check minimum spend or product rules)'
              )
            );
            return;
          }

          showDiscountMessage(
            'error',
            getDiscountMessageText('invalid', 'This discount code is invalid or not eligible')
          );
        })
        .catch(function(error) {
          console.error('Error applying discount:', error);
          showDiscountMessage(
            'error',
            getDiscountMessageText('error', 'Could not apply the discount code. Please try again')
          );
        })
        .finally(function() {
          discountRequestInFlight = false;
          // 成功刷新后按钮节点可能被替换；失败时恢复当前按钮
          if (document.body.contains(applyDiscountBtn)) {
            applyDiscountBtn.disabled = false;
            applyDiscountBtn.textContent = originalText;
          }
        });
      }
      
      if (e.target.matches('[data-action="remove-discount"]')) {
        e.preventDefault();

        if (discountRequestInFlight) {
          return;
        }
        
        const removeDiscountBtn = e.target;
        
        clearDiscountMessage();
        discountRequestInFlight = true;
        removeDiscountBtn.disabled = true;
        const originalText = removeDiscountBtn.textContent;
        removeDiscountBtn.textContent = getDiscountMessageText('removing', 'Removing...');
  
        updateCartDiscount('')
        .then(function() {
          refreshMiniCart();
        })
        .catch(function(error) {
          console.error('Error removing discount:', error);
          showDiscountMessage(
            'error',
            getDiscountMessageText('remove-error', 'Could not remove the discount code. Please try again')
          );
        })
        .finally(function() {
          discountRequestInFlight = false;
          if (document.body.contains(removeDiscountBtn)) {
            removeDiscountBtn.disabled = false;
            removeDiscountBtn.textContent = originalText;
          }
        });
      }
    });
  
    // 回车键应用折扣码
    document.addEventListener('keypress', function(e) {
      if (e.target.matches('#discount-code-input') && e.key === 'Enter') {
        e.preventDefault();
        const applyBtn = document.querySelector('[data-action="apply-discount"]');
        if (applyBtn && !applyBtn.disabled && !discountRequestInFlight) {
          applyBtn.click();
        }
      }
    });


    // 同步公告栏消息到迷你购物车的折扣提示
  function syncAnnouncementToMiniCart() {
  try {
    // 获取所有公告栏消息元素
    var messageElements = document.querySelectorAll('.announcement-bar__message p');
    
    // 如果没有找到 p 标签，则尝试直接获取 .announcement-bar__message
    if (messageElements.length === 0) {
      messageElements = document.querySelectorAll('.announcement-bar__message');
    }

    // 如果仍然没有找到元素则返回
    if (messageElements.length === 0) return;

    var latestDiscountEl = document.querySelector('.mini-cart__discount-code .latest-discount');
    if (!latestDiscountEl) return;

    // 清空现有内容
    latestDiscountEl.innerHTML = '';

    // 处理所有公告栏消息
    messageElements.forEach(function(messageEl, index) {
      // 为每个消息创建容器元素
      var messageContainer = document.createElement('div');
      messageContainer.className = 'announcement-message-item';
      
      // 使用 innerHTML 保留后台设置中的加粗等富文本格式
      messageContainer.innerHTML = messageEl.innerHTML || messageEl.textContent || '';
      
      // 将消息添加到折扣区域
      latestDiscountEl.appendChild(messageContainer);
      
    });

  } catch (e) {
    console.error('Failed to sync announcement message to mini cart:', e);
  }
}

  // 初次加载同步一次
  syncAnnouncementToMiniCart();

  // 在渲染结束后再同步，避免被重渲染覆盖
  function scheduleSyncAnnouncementToMiniCart() {
    requestAnimationFrame(function() {
      setTimeout(syncAnnouncementToMiniCart, 0);
    });
  }

  // 购物车变更后可能会重新渲染抽屉，变更时再同步一次
  document.addEventListener('cart:updated', function() {
    scheduleSyncAnnouncementToMiniCart();
  });

  // 商品添加到购物车时也同步（有些场景先触发 variant:added 再刷新 cart）
  document.addEventListener('variant:added', function() {
    scheduleSyncAnnouncementToMiniCart();
  });

  // 如果外部代码触发 cart:refresh，我们也在刷新后同步
  document.addEventListener('cart:refresh', function() {
    scheduleSyncAnnouncementToMiniCart();
  });

  // 若主题在打开抽屉时动态插入内容，可在下一帧再尝试同步
  document.addEventListener('click', function(e) {
    var toggleTargets = ['[aria-controls="mini-cart"]', '#mini-cart-toggle', '.mini-cart-toggle'];
    if (toggleTargets.some(function(sel){ return e.target.closest(sel); })) {
      scheduleSyncAnnouncementToMiniCart();
    }
  });

  // 观察迷你购物车节点的重渲染，变化后自动同步
  var miniCartEl = document.getElementById('mini-cart');
  if (miniCartEl && 'MutationObserver' in window) {
    var syncTimeout;
    var observer = new MutationObserver(function() {
      clearTimeout(syncTimeout);
      syncTimeout = setTimeout(syncAnnouncementToMiniCart, 50);
    });
    observer.observe(miniCartEl, { childList: true, subtree: true });
  }

  // Footer menu 添加到购物车按钮处理
  // 直接提交产品表单添加到购物车
  document.addEventListener('click', function(e) {
    var footerMenuAddToCart = e.target.closest('.footer-menu__add-to-cart-button');
    if (footerMenuAddToCart) {
      e.preventDefault();
      
      // 查找页面上的产品表单
      var productForm = document.querySelector('form[is="product-form"]');
      if (!productForm) {
        console.warn('Product form not found');
        return;
      }
      
      // 使用 requestSubmit 方法，这会触发验证和提交
      // 如果表单无效（例如未选择变体），会自动显示验证提示
      if (productForm.requestSubmit) {
        productForm.requestSubmit();
      } else {
        // 降级方案：手动触发 submit 事件
        var submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        productForm.dispatchEvent(submitEvent);
      }
    }
  });

});